import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { getConsent, setConsent, subscribeConsent } from "../lib/consent";
import { isEURegime } from "../lib/region";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation("common");

  // Spontaneous display is EU-regime only (non-EU visitors get cookies
  // without a banner); the reopen path below stays universal so the
  // privacy page "manage cookies" link works for everyone.
  useEffect(() => {
    if (!isEURegime() || getConsent()) return undefined;
    const timer = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  // The privacy page can clear the stored choice ("manage cookies") — the
  // banner must come back without a reload.
  useEffect(
    () => subscribeConsent((value) => setVisible(value === null)),
    []
  );

  const decide = (value) => {
    setConsent(value);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("cookieBanner.ariaLabel")}
      className="cookie-banner-enter fixed bottom-4 right-4 z-50"
    >
      <div className="glass relative flex max-w-[calc(100vw-2rem)] flex-col gap-2 rounded-lg p-3 shadow-2xl sm:max-w-sm">
        <button
          type="button"
          onClick={() => decide("declined")}
          aria-label={t("cookieBanner.close")}
          className="absolute -right-1 -top-1 rounded-full border border-white/10 bg-[#1d1d21] p-1 text-[var(--text-muted)] transition-colors hover:bg-[#2a2a30] hover:text-white"
        >
          <X size={14} />
        </button>
        <p className="pr-4 font-body text-sm leading-relaxed text-[var(--text-muted)]">
          {t("cookieBanner.message")}{" "}
          <Link
            to="/privacy"
            className="underline underline-offset-2 hover:text-white"
          >
            {t("cookieBanner.privacyPolicy")}
          </Link>
        </p>
        <div className="flex flex-row gap-2">
          <button
            type="button"
            onClick={() => decide("declined")}
            className="rounded-md px-3 py-1.5 text-xs text-[var(--text-muted)] transition-colors hover:bg-white/5 hover:text-white"
          >
            {t("cookieBanner.reject")}
          </button>
          <button
            type="button"
            onClick={() => decide("accepted")}
            className="btn-primary flex-1 rounded-md px-3 py-1.5 text-xs"
          >
            {t("cookieBanner.acceptAll")}
          </button>
        </div>
      </div>
    </div>
  );
}
